import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Table,
  Badge,
  Alert,
  Spinner,
  Form,
  InputGroup,
  Pagination,
  Tabs,
  Tab,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { fires } from "../../firebase";
import firebase from "firebase";
import { CSVLink } from "react-csv";
import { FaDownload, FaSync, FaCalendarAlt } from "react-icons/fa";

export default function AdminYearReview() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Year selection
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  
  // Generate available years (current year and previous years)
  const generateAvailableYears = () => {
    const currentYear = new Date().getFullYear();
    const startYear = 2020; // Start from 2020, or adjust as needed
    const years = [];
    for (let year = currentYear; year >= startYear; year--) {
      years.push(year);
    }
    return years;
  };

  const [availableYears, setAvailableYears] = useState(generateAvailableYears())
  
  
  // Data states
  const [orders, setOrders] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  
  // Search and filter
  const [searchTerm, setSearchTerm] = useState("");
  const [orderFilterStatus, setOrderFilterStatus] = useState("all");
  const [activeTab, setActiveTab] = useState("orders");
  
  // Pagination
  const [ordersCurrentPage, setOrdersCurrentPage] = useState(1);
  const [ticketsCurrentPage, setTicketsCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  
  // Analytics
  const [analytics, setAnalytics] = useState({
    totalOrders: 0,
    totalTickets: 0,
    totalOrganizations: 0,
    totalRevenue: 0,
    completedOrders: 0,
    pendingOrders: 0,
    refundedOrders: 0,
    cancelledOrders: 0,
    ordersByMonth: {},
    ticketsByMonth: {},
    revenueByMonth: {},
  });

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        if (!currentUser) {
          navigate('/');
          return;
        }
        
        const adminDoc = await fires.collection('admin').doc(currentUser.uid).get();
        if (!adminDoc.exists) {
          setIsAdmin(false);
          setLoading(false);
          navigate('/');
          return;
        }
        
        const adminData = adminDoc.data();
        if (!adminData.roles || !adminData.roles.includes('management')) {
          setIsAdmin(false);
          setLoading(false);
          navigate('/');
          return;
        }
        
        setIsAdmin(true);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setError('Error verifying admin access');
        setLoading(false);
        navigate('/');
      }
    };

    checkAdminStatus();
  }, [currentUser, navigate]);

  // Load data when year changes
  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [selectedYear, isAdmin]);

  // Filter data when search or filters change
  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm, orderFilterStatus]);

  useEffect(() => {
    filterTickets();
  }, [tickets, searchTerm]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      // Calculate date range for selected year
      const startDate = new Date(selectedYear, 0, 1); // January 1st
      const startTimestamp = firebase.firestore.Timestamp.fromDate(startDate);
      const endDate = new Date(selectedYear, 11, 31, 23, 59, 59); // December 31st
      const endTimestamp = firebase.firestore.Timestamp.fromDate(endDate);

      // Load orders for the year
      const ordersSnapshot = await fires
        .collection("orders")
        .where("timestamp", ">=", startTimestamp)
        .where("timestamp", "<=", endTimestamp)
        .orderBy("timestamp", "desc")
        .get();

      const ordersData = [];
      ordersSnapshot.docs.forEach((doc) => {
        const orderData = doc.data();
        ordersData.push({
          id: doc.id,
          ...orderData,
          createdAt: orderData.timestamp?.toDate?.() || new Date(1970, 0, 1),
        });
      });
      setOrders(ordersData);

      // Load tickets for the year
      // Tickets are stored in events/{eventId}/tickets subcollections
      const ticketsData = [];
      const eventsSnapshot = await fires.collection("events").get();
      
      for (const eventDoc of eventsSnapshot.docs) {
        const ticketsSnapshot = await fires
          .collection("events")
          .doc(eventDoc.id)
          .collection("tickets")
          .where("timestamp", ">=", startTimestamp)
          .where("timestamp", "<=", endTimestamp)
          .orderBy("timestamp", "desc")
          .get();

        ticketsSnapshot.docs.forEach((ticketDoc) => {
          const ticketData = ticketDoc.data();
          ticketsData.push({
            id: ticketDoc.id,
            eventId: eventDoc.id,
            eventTitle: eventDoc.data().title || "Unknown Event",
            ...ticketData,
            createdAt: ticketData.timestamp?.toDate?.() || new Date(1970, 0, 1),
          });
        });
      }
      
      // Sort tickets by timestamp
      ticketsData.sort((a, b) => b.createdAt - a.createdAt);
      setTickets(ticketsData);

      // Load organizations (groups) for the year
      const groupsSnapshot = await fires
        .collection("groups")
        .where("timestamp", ">=", startTimestamp)
        .where("timestamp", "<=", endTimestamp)
        .get();

      const totalOrganizations = groupsSnapshot.docs.length;

      // Calculate available years
      calculateAvailableYears(ordersData, ticketsData);

      // Calculate analytics
      calculateAnalytics(ordersData, ticketsData, totalOrganizations);
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateAvailableYears = (ordersData, ticketsData) => {
    const yearsSet = new Set();
    
    ordersData.forEach((order) => {
      if (order.createdAt) {
        yearsSet.add(order.createdAt.getFullYear());
      }
    });
    
    ticketsData.forEach((ticket) => {
      if (ticket.createdAt) {
        yearsSet.add(ticket.createdAt.getFullYear());
      }
    });
    
    const years = Array.from(yearsSet).sort((a, b) => b - a);
    setAvailableYears(years.length > 0 ? years : [new Date().getFullYear()]);
  };

  const calculateAnalytics = (ordersData, ticketsData, totalOrganizations = 0) => {
    const analyticsData = {
      totalOrders: ordersData.length,
      totalTickets: ticketsData.length,
      totalOrganizations: totalOrganizations,
      totalRevenue: 0,
      completedOrders: 0,
      pendingOrders: 0,
      refundedOrders: 0,
      cancelledOrders: 0,
      ordersByMonth: {},
      ticketsByMonth: {},
      revenueByMonth: {},
    };

    // Process orders
    ordersData.forEach((order) => {
      const month = order.createdAt.getMonth();
      const monthKey = `${selectedYear}-${String(month + 1).padStart(2, "0")}`;
      
      if (!analyticsData.ordersByMonth[monthKey]) {
        analyticsData.ordersByMonth[monthKey] = 0;
        analyticsData.revenueByMonth[monthKey] = 0;
      }
      analyticsData.ordersByMonth[monthKey]++;

      const amount = order.amount || 0;
      analyticsData.totalRevenue += amount;
      analyticsData.revenueByMonth[monthKey] += amount;

      const status = (order.status || "").toLowerCase();
      if (status === "completed" || status === "paid") {
        analyticsData.completedOrders++;
      } else if (status === "pending") {
        analyticsData.pendingOrders++;
      } else if (status === "refunded") {
        analyticsData.refundedOrders++;
      } else if (status === "cancelled" || status === "canceled") {
        analyticsData.cancelledOrders++;
      }
    });

    // Process tickets
    ticketsData.forEach((ticket) => {
      const month = ticket.createdAt.getMonth();
      const monthKey = `${selectedYear}-${String(month + 1).padStart(2, "0")}`;
      
      if (!analyticsData.ticketsByMonth[monthKey]) {
        analyticsData.ticketsByMonth[monthKey] = 0;
      }
      analyticsData.ticketsByMonth[monthKey]++;
    });

    setAnalytics(analyticsData);
  };

  const filterOrders = () => {
    let filtered = orders;

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          order.id?.toLowerCase().includes(searchLower) ||
          order.userEmail?.toLowerCase().includes(searchLower) ||
          order.eventTitle?.toLowerCase().includes(searchLower) ||
          order.status?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by status
    if (orderFilterStatus !== "all") {
      filtered = filtered.filter(
        (order) => (order.status || "").toLowerCase() === orderFilterStatus.toLowerCase()
      );
    }

    setFilteredOrders(filtered);
    setOrdersCurrentPage(1);
  };

  const filterTickets = () => {
    let filtered = tickets;

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (ticket) =>
          ticket.id?.toLowerCase().includes(searchLower) ||
          ticket.eventTitle?.toLowerCase().includes(searchLower) ||
          ticket.firstName?.toLowerCase().includes(searchLower) ||
          ticket.lastName?.toLowerCase().includes(searchLower) ||
          ticket.email?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredTickets(filtered);
    setTicketsCurrentPage(1);
  };

  const formatCurrency = (amount) => {
    if (typeof amount !== "number") return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "CAD",
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    if (date.toDate) {
      return date.toDate().toLocaleString();
    }
    if (date instanceof Date) {
      return date.toLocaleString();
    }
    return "N/A";
  };

  // Pagination for orders
  const ordersIndexOfLast = ordersCurrentPage * itemsPerPage;
  const ordersIndexOfFirst = ordersIndexOfLast - itemsPerPage;
  const currentOrders = filteredOrders.slice(ordersIndexOfFirst, ordersIndexOfLast);
  const ordersTotalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  // Pagination for tickets
  const ticketsIndexOfLast = ticketsCurrentPage * itemsPerPage;
  const ticketsIndexOfFirst = ticketsIndexOfLast - itemsPerPage;
  const currentTickets = filteredTickets.slice(ticketsIndexOfFirst, ticketsIndexOfLast);
  const ticketsTotalPages = Math.ceil(filteredTickets.length / itemsPerPage);

  // Prepare CSV data
  const ordersCSVData = filteredOrders.map((order) => ({
    "Order ID": order.id,
    "Date": formatDate(order.createdAt),
    "User Email": order.userEmail || "N/A",
    "Event Title": order.eventTitle || "N/A",
    "Amount": formatCurrency(order.amount || 0),
    "Status": order.status || "N/A",
    "Payment Method": order.paymentMethod || "N/A",
  }));

  const ticketsCSVData = filteredTickets.map((ticket) => ({
    "Ticket ID": ticket.id,
    "Date": formatDate(ticket.createdAt),
    "Event Title": ticket.eventTitle || "N/A",
    "First Name": ticket.firstName || "N/A",
    "Last Name": ticket.lastName || "N/A",
    "Email": ticket.email || "N/A",
    "Ticket Type": ticket.ticketType || "N/A",
    "Price": formatCurrency(ticket.price || 0),
    "Validated": ticket.validated ? "Yes" : "No",
  }));

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading year reviews...</p>
        </div>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">Access denied. Admin privileges required.</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h3 className="mb-0">
                <FaCalendarAlt className="me-2" />
                Year Reviews - {selectedYear}
              </h3>
              <div>
                <Button
                  variant="outline-primary"
                  onClick={loadData}
                  className="me-2"
                >
                  <FaSync className="me-2" />
                  Refresh
                </Button>
                {activeTab === "orders" && (
                  <CSVLink
                    data={ordersCSVData}
                    filename={`orders-${selectedYear}.csv`}
                    className="btn btn-outline-success"
                  >
                    <FaDownload className="me-2" />
                    Export Orders CSV
                  </CSVLink>
                )}
                {activeTab === "tickets" && (
                  <CSVLink
                    data={ticketsCSVData}
                    filename={`tickets-${selectedYear}.csv`}
                    className="btn btn-outline-success"
                  >
                    <FaDownload className="me-2" />
                    Export Tickets CSV
                  </CSVLink>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}

              {/* Year Selection */}
              <Row className="mb-4">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Select Year</Form.Label>
                    <Form.Select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    >
                      {availableYears.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Analytics Summary */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <Card.Title>Total Orders</Card.Title>
                      <h2>{analytics.totalOrders}</h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <Card.Title>Total Tickets</Card.Title>
                      <h2>{analytics.totalTickets}</h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <Card.Title>Total Groups</Card.Title>
                      <h2>{analytics.totalOrganizations}</h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <Card.Title>Total Revenue</Card.Title>
                      <h2>{formatCurrency(analytics.totalRevenue)}</h2>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <Card.Title>Completed Orders</Card.Title>
                      <h2>{analytics.completedOrders}</h2>
                    </Card.Body>
                  </Card>
                </Col>
                </Row>

              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <Card.Title>Pending Orders</Card.Title>
                      <h2>{analytics.pendingOrders}</h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <Card.Title>Refunded Orders</Card.Title>
                      <h2>{analytics.refundedOrders}</h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <Card.Title>Cancelled Orders</Card.Title>
                      <h2>{analytics.cancelledOrders}</h2>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Search and Filter */}
              <Row className="mb-3">
                <Col md={6}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                {activeTab === "orders" && (
                  <Col md={3}>
                    <Form.Select
                      value={orderFilterStatus}
                      onChange={(e) => setOrderFilterStatus(e.target.value)}
                    >
                      <option value="all">All Statuses</option>
                      <option value="completed">Completed</option>
                      <option value="paid">Paid</option>
                      <option value="pending">Pending</option>
                      <option value="refunded">Refunded</option>
                      <option value="cancelled">Cancelled</option>
                    </Form.Select>
                  </Col>
                )}
              </Row>

              {/* Tabs */}
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-3"
              >
                <Tab eventKey="orders" title={`Orders (${filteredOrders.length})`}>
                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Order ID</th>
                          <th>Date</th>
                          <th>User Email</th>
                          <th>Event Title</th>
                          <th>Amount</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentOrders.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center">
                              No orders found for {selectedYear}
                            </td>
                          </tr>
                        ) : (
                          currentOrders.map((order) => (
                            <tr key={order.id}>
                              <td>{order.id}</td>
                              <td>{formatDate(order.createdAt)}</td>
                              <td>{order.userEmail || "N/A"}</td>
                              <td>{order.eventTitle || "N/A"}</td>
                              <td>{formatCurrency(order.amount || 0)}</td>
                              <td>
                                <Badge
                                  bg={
                                    (order.status || "").toLowerCase() === "completed" ||
                                    (order.status || "").toLowerCase() === "paid"
                                      ? "success"
                                      : (order.status || "").toLowerCase() === "pending"
                                      ? "warning"
                                      : (order.status || "").toLowerCase() === "refunded"
                                      ? "info"
                                      : (order.status || "").toLowerCase() === "cancelled" ||
                                        (order.status || "").toLowerCase() === "canceled"
                                      ? "danger"
                                      : "secondary"
                                  }
                                >
                                  {order.status || "N/A"}
                                </Badge>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                  {ordersTotalPages > 1 && (
                    <Pagination className="mt-3">
                      <Pagination.First
                        onClick={() => setOrdersCurrentPage(1)}
                        disabled={ordersCurrentPage === 1}
                      />
                      <Pagination.Prev
                        onClick={() => setOrdersCurrentPage(ordersCurrentPage - 1)}
                        disabled={ordersCurrentPage === 1}
                      />
                      {Array.from({ length: ordersTotalPages }, (_, i) => i + 1)
                        .filter(
                          (page) =>
                            page === 1 ||
                            page === ordersTotalPages ||
                            (page >= ordersCurrentPage - 2 &&
                              page <= ordersCurrentPage + 2)
                        )
                        .map((page, index, array) => (
                          <React.Fragment key={page}>
                            {index > 0 && array[index - 1] !== page - 1 && (
                              <Pagination.Ellipsis />
                            )}
                            <Pagination.Item
                              active={page === ordersCurrentPage}
                              onClick={() => setOrdersCurrentPage(page)}
                            >
                              {page}
                            </Pagination.Item>
                          </React.Fragment>
                        ))}
                      <Pagination.Next
                        onClick={() => setOrdersCurrentPage(ordersCurrentPage + 1)}
                        disabled={ordersCurrentPage === ordersTotalPages}
                      />
                      <Pagination.Last
                        onClick={() => setOrdersCurrentPage(ordersTotalPages)}
                        disabled={ordersCurrentPage === ordersTotalPages}
                      />
                    </Pagination>
                  )}
                </Tab>

                <Tab eventKey="tickets" title={`Tickets (${filteredTickets.length})`}>
                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Ticket ID</th>
                          <th>Date</th>
                          <th>Event Title</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Ticket Type</th>
                          <th>Price</th>
                          <th>Validated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentTickets.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center">
                              No tickets found for {selectedYear}
                            </td>
                          </tr>
                        ) : (
                          currentTickets.map((ticket) => (
                            <tr key={ticket.id}>
                              <td>{ticket.id}</td>
                              <td>{formatDate(ticket.createdAt)}</td>
                              <td>{ticket.eventTitle || "N/A"}</td>
                              <td>
                                {ticket.firstName || ""} {ticket.lastName || ""}
                              </td>
                              <td>{ticket.email || "N/A"}</td>
                              <td>{ticket.ticketType || "N/A"}</td>
                              <td>{formatCurrency(ticket.price || 0)}</td>
                              <td>
                                <Badge bg={ticket.validated ? "success" : "warning"}>
                                  {ticket.validated ? "Yes" : "No"}
                                </Badge>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                  {ticketsTotalPages > 1 && (
                    <Pagination className="mt-3">
                      <Pagination.First
                        onClick={() => setTicketsCurrentPage(1)}
                        disabled={ticketsCurrentPage === 1}
                      />
                      <Pagination.Prev
                        onClick={() => setTicketsCurrentPage(ticketsCurrentPage - 1)}
                        disabled={ticketsCurrentPage === 1}
                      />
                      {Array.from({ length: ticketsTotalPages }, (_, i) => i + 1)
                        .filter(
                          (page) =>
                            page === 1 ||
                            page === ticketsTotalPages ||
                            (page >= ticketsCurrentPage - 2 &&
                              page <= ticketsCurrentPage + 2)
                        )
                        .map((page, index, array) => (
                          <React.Fragment key={page}>
                            {index > 0 && array[index - 1] !== page - 1 && (
                              <Pagination.Ellipsis />
                            )}
                            <Pagination.Item
                              active={page === ticketsCurrentPage}
                              onClick={() => setTicketsCurrentPage(page)}
                            >
                              {page}
                            </Pagination.Item>
                          </React.Fragment>
                        ))}
                      <Pagination.Next
                        onClick={() => setTicketsCurrentPage(ticketsCurrentPage + 1)}
                        disabled={ticketsCurrentPage === ticketsTotalPages}
                      />
                      <Pagination.Last
                        onClick={() => setTicketsCurrentPage(ticketsTotalPages)}
                        disabled={ticketsCurrentPage === ticketsTotalPages}
                      />
                    </Pagination>
                  )}
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
